import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import useAuthStore from '@/store/authStore';
import apiClient from '@/lib/api';
import { 
  LayoutDashboard, User, Shield, Monitor, Link as LinkIcon, 
  Code, Bell, Settings, LogOut, Menu, X, ShieldCheck 
} from 'lucide-react';
import { useState } from 'react';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      navigate('/login');
    }
  };

  const navigation = [
    { name: 'Overview', to: '/dashboard', icon: LayoutDashboard, testId: 'nav-overview', end: true },
    { name: 'Profile', to: '/dashboard/profile', icon: User, testId: 'nav-profile' },
    { name: 'Security', to: '/dashboard/security', icon: Shield, testId: 'nav-security' },
    { name: 'Sessions', to: '/dashboard/sessions', icon: Monitor, testId: 'nav-sessions' },
    { name: 'Connected Apps', to: '/dashboard/apps', icon: LinkIcon, testId: 'nav-apps' },
  ];

  const adminNav = user?.is_admin ? [
    { name: 'Developer', to: '/dashboard/developer', icon: Code, testId: 'nav-developer' },
    { name: 'Admin Panel', to: '/dashboard/admin', icon: ShieldCheck, testId: 'nav-admin' }
  ] : [];

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-card border-r border-border
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="dashboard-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold tracking-tight">
                <span className="text-pista">Lost</span>DrkC
              </h1>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-muted-foreground"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Account Platform</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 rounded-lg transition-colors duration-200 ${
                    isActive
                      ? 'bg-pista/15 text-pista'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`
                }
                data-testid={item.testId}
              >
                <item.icon className="h-5 w-5 mr-3" />
                <span className="font-medium">{item.name}</span>
              </NavLink>
            ))}
            {adminNav.length > 0 && (
              <>
                <div className="pt-4 pb-2 px-4">
                  <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    Administration
                  </div>
                </div>
                {adminNav.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-3 rounded-lg transition-colors duration-200 ${
                        isActive
                          ? 'bg-pista/15 text-pista'
                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                      }`
                    }
                    data-testid={item.testId}
                  >
                    <item.icon className="h-5 w-5 mr-3" />
                    <span className="font-medium">{item.name}</span>
                  </NavLink>
                ))}
              </>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center space-x-3 mb-3">
              <Avatar>
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-pista/20 text-pista">{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-card/95 backdrop-blur-sm border-b border-border px-4 py-3">
          <button onClick={() => setSidebarOpen(true)} className="text-foreground">
            <Menu className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
